<template>
  <div class="event-detail-page">
    <template v-if="event">
      <div class="content-wrap">
        <button class="back-btn" @click="goBack">← 返回活动列表</button>

        <img
          :src="event.image || `https://picsum.photos/seed/${event.id}/800/320`"
          :alt="event.title"
          class="event-image"
        />

        <div class="event-tags">
          <span class="event-status" :style="{ background: event.statusColor, color: '#fff' }">
            {{ event.status }}
          </span>
          <span class="event-type">{{ event.type }}</span>
        </div>

        <h1 class="event-title">{{ event.title }}</h1>
        <div class="event-meta">
          <span><Icon icon="lucide:calendar" :size="14" /> {{ event.date }}</span>
          <span><Icon icon="lucide:map-pin" :size="14" /> {{ event.location }}</span>
        </div>
        <p class="event-desc">{{ event.desc }}</p>

        <!-- Registration Form -->
        <div class="form-card">
          <h3 class="form-title">活动报名</h3>
          <div class="form-grid-2">
            <div class="form-group">
              <label class="form-label">姓名 *</label>
              <input v-model="form.name" class="form-input" placeholder="请输入姓名" />
            </div>
            <div class="form-group">
              <label class="form-label">公司名称 *</label>
              <input v-model="form.company" class="form-input" placeholder="请输入公司名称" />
            </div>
            <div class="form-group">
              <label class="form-label">联系电话 *</label>
              <input
                v-model="form.phone"
                class="form-input"
                type="tel"
                placeholder="请输入手机号"
              />
            </div>
            <div class="form-group">
              <label class="form-label">邮箱地址 *</label>
              <input
                v-model="form.email"
                class="form-input"
                type="email"
                placeholder="请输入邮箱"
              />
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">备注需求</label>
            <textarea v-model="form.note" class="form-textarea" placeholder="如有特殊需求请注明" />
          </div>
          <button class="submit-btn" @click="handleSubmit">提交报名</button>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="fallback-wrap">
        <h2 class="fallback-title">活动未找到</h2>
        <p class="fallback-desc">该活动不存在或已结束</p>
        <button class="fallback-btn" @click="goBack">← 返回活动列表</button>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { reactive, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Icon } from '@/components/Icon'
import { events } from '../data/portalData'
import { getSubmitErrorMessage, submitPortalForm } from '../usePortalSubmission'

defineOptions({ name: 'EventDetailPage' })

const route = useRoute()
const router = useRouter()

const eventId = computed(() => route.params.id as string)
const event = computed(() => events.find((e) => e.id === eventId.value))

const form = reactive({
  name: '',
  company: '',
  phone: '',
  email: '',
  note: ''
})

function goBack() {
  router.push('/portal/events')
}

async function handleSubmit() {
  if (!form.name || !form.company || !form.phone || !form.email) {
    ElMessage.warning('请填写姓名、公司名称、联系电话和邮箱')
    return
  }
  try {
    await submitPortalForm({
      username: form.name,
      companyName: form.company,
      tel: form.phone,
      email: form.email,
      content: form.note,
      productName: event.value?.title,
      type: 'EVENT_REGISTRATION'
    })
    ElMessage.success('报名成功！活动前会以短信和邮件通知您')
    Object.assign(form, { name: '', company: '', phone: '', email: '', note: '' })
  } catch (error) {
    ElMessage.error(getSubmitErrorMessage(error))
  }
}
</script>

<style lang="scss" scoped>
.content-wrap {
  padding: 64px 24px 80px;
  max-width: 800px;
  margin: 0 auto;
}

.back-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #0066ff;
  font-size: 14px;
  margin-bottom: 24px;

  &:hover {
    opacity: 0.7;
  }
}

.event-image {
  width: 100%;
  height: 320px;
  object-fit: cover;
  border-radius: 16px;
  margin-bottom: 24px;
  display: block;
}

.event-tags {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.event-status {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 4px;
  font-weight: 600;
}

.event-type {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 4px;
  font-weight: 500;
  background: #f3f4f6;
  color: #4b5563;
}

.event-title {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 12px;
  color: #111827;
}

.event-meta {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
  font-size: 14px;
  color: #6b7280;

  span {
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.event-desc {
  font-size: 16px;
  line-height: 1.8;
  color: #4b5563;
  margin-bottom: 32px;
}

.form-card {
  background: #f9fafb;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #e5e7eb;
}

.form-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 20px;
}

.form-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #0066ff;
  }
  &::placeholder {
    color: #9ca3af;
  }
}

.form-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    border-color: #0066ff;
  }
  &::placeholder {
    color: #9ca3af;
  }
}

.submit-btn {
  height: 48px;
  padding: 0 40px;
  background: #0066ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #0044cc;
  }
}

.fallback-wrap {
  text-align: center;
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.fallback-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 12px;
}
.fallback-desc {
  color: #6b7280;
  margin-bottom: 24px;
}

.fallback-btn {
  background: none;
  border: 1px solid #0066ff;
  color: #0066ff;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: #0066ff;
    color: #fff;
  }
}

@media (max-width: 768px) {
  .form-grid-2 {
    grid-template-columns: 1fr;
  }
  .event-title {
    font-size: 24px;
  }
}
</style>
